import { useState, useEffect, useCallback, useMemo } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Clock, Printer, RefreshCw } from "lucide-react";

/**
 * Shared time-card view. Self-service (prescriber profile) when `userId` is
 * unset; admin-view time card (for any staff) when `userId` is provided.
 *
 * Props:
 *   userId    - (optional) filter punches to a specific staff member (admin only)
 *   userName  - (optional) label shown in the printable header
 *   userRole  - (optional) label shown in the printable header
 *   employeeId- (optional) label shown in the printable header
 */
export default function TimeCard({ userId = "", userName = "", userRole = "", employeeId = "" }) {
  const [entries, setEntries] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (userId) params.user_id = userId;
      const { data } = await API.get("/timeclock/history", { params });
      setEntries(data);
    } catch { setEntries([]); } finally { setLoading(false); }
  }, [days, userId]);

  useEffect(() => { load(); }, [load]);

  /**
   * Group punches by calendar day (local time). Each row = one day with N
   * punch pairs + a daily subtotal. Cleaner than a flat list when printing.
   */
  const days_grouped = useMemo(() => {
    const by_day = new Map();
    for (const e of entries) {
      if (!e.clock_in) continue;
      const d = new Date(e.clock_in);
      const key = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
      if (!by_day.has(key)) by_day.set(key, []);
      by_day.get(key).push(e);
    }
    return [...by_day.entries()].map(([date, punches]) => {
      let ms = 0;
      for (const p of punches) {
        if (p.clock_in && p.clock_out) {
          try { ms += new Date(p.clock_out) - new Date(p.clock_in); } catch { /* ignore */ }
        }
      }
      return { date, punches, hours: ms / (1000 * 60 * 60), has_open: punches.some(p => !p.clock_out) };
    });
  }, [entries]);

  const total_hours = useMemo(() => days_grouped.reduce((s, d) => s + d.hours, 0), [days_grouped]);

  const format_dur = (h) => {
    if (!h || h < 0) return "—";
    const hr = Math.floor(h);
    const mn = Math.round((h - hr) * 60);
    return `${hr}h ${mn}m`;
  };
  const format_time = (iso) => iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "—";

  // Escape anything we interpolate into the print HTML. Prevents XSS if a
  // patient or staff name ever contains angle brackets, quotes, or ampersands.
  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));

  const handle_print = () => {
    const title = userName ? `Time Card — ${userName}` : "My Time Card";
    const now_str = new Date().toLocaleString();
    const rows = days_grouped.map(d => {
      const punches_html = d.punches.map(p => `
        <tr>
          <td style="padding:6px 10px;border-top:1px solid #f1f5f9;color:#475569;font-size:12px;">${esc(format_time(p.clock_in))}</td>
          <td style="padding:6px 10px;border-top:1px solid #f1f5f9;color:#475569;font-size:12px;">${p.clock_out ? esc(format_time(p.clock_out)) : '<span style="color:#d97706;font-weight:600;">Still clocked in</span>'}</td>
          <td style="padding:6px 10px;border-top:1px solid #f1f5f9;color:#475569;font-size:12px;text-align:right;">${p.clock_in && p.clock_out ? esc(format_dur((new Date(p.clock_out) - new Date(p.clock_in)) / (1000 * 60 * 60))) : "—"}</td>
        </tr>`).join("");
      return `
        <tr>
          <td colspan="3" style="padding:12px 10px 4px;background:#f8fafc;font-weight:600;color:#1e293b;font-size:13px;">
            ${esc(d.date)}  <span style="color:#7c3aed;font-weight:700;font-size:12px;margin-left:12px;">${esc(format_dur(d.hours))}</span>
          </td>
        </tr>
        ${punches_html}`;
    }).join("");

    const html = `<!doctype html><html><head><title>${esc(title)}</title>
<style>
  body{font-family:'DM Sans',Arial,sans-serif;max-width:720px;margin:24px auto;color:#0f172a;}
  h1{margin:0 0 4px;font-size:22px;}
  .meta{color:#64748b;font-size:12px;margin-bottom:16px;}
  .totals{background:#ede9fe;border:1px solid #c4b5fd;border-radius:10px;padding:14px 18px;margin-bottom:18px;display:flex;justify-content:space-between;align-items:center;}
  .totals .h{font-size:28px;font-weight:700;color:#5b21b6;}
  table{width:100%;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;}
  th{text-align:left;padding:10px;background:#1e293b;color:white;font-size:11px;text-transform:uppercase;letter-spacing:.05em;}
  th.r{text-align:right;}
  tfoot td{background:#0f172a;color:white;padding:10px;font-weight:600;}
  @media print{body{margin:0;}.no-print{display:none;}}
</style></head><body>
  <h1>${esc(title)}</h1>
  <div class="meta">
    ${userRole ? `Role: ${esc(userRole)}<br>` : ""}
    ${employeeId ? `Employee ID: <span style="font-family:monospace;">${esc(employeeId)}</span><br>` : ""}
    Window: last ${esc(days)} days &middot; Printed ${esc(now_str)}
  </div>
  <div class="totals">
    <div><div style="font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#5b21b6;">Total hours</div><div class="h">${esc(format_dur(total_hours))}</div></div>
    <div style="text-align:right;font-size:12px;color:#5b21b6;">${esc(days_grouped.length)} day(s) &middot; ${esc(entries.length)} punch(es)</div>
  </div>
  <table>
    <thead><tr><th>Clock In</th><th>Clock Out</th><th class="r">Duration</th></tr></thead>
    <tbody>${rows || `<tr><td colspan="3" style="padding:24px;text-align:center;color:#94a3b8;">No entries</td></tr>`}</tbody>
    <tfoot><tr><td colspan="2">Total</td><td style="text-align:right;">${esc(format_dur(total_hours))}</td></tr></tfoot>
  </table>
  <script>window.onload=()=>{window.print();}<\/script>
</body></html>`;

    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) return;
    // Render the print preview into a new tab via iframe.srcdoc rather than
    // document.write. The HTML is server-controlled (the staff member's own
    // time entries) and we explicitly escape user-controlled fields above.
    // Iframe needs allow-scripts + allow-modals so the auto-print onload
    // handler can call window.print() inside the sandbox.
    const iframe = w.document.createElement("iframe");
    iframe.setAttribute("sandbox", "allow-same-origin allow-scripts allow-modals");
    iframe.style.cssText = "border:0;width:100%;height:100vh;";
    iframe.srcdoc = html;
    w.document.body.style.margin = "0";
    w.document.body.appendChild(iframe);
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6" data-testid="timecard">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center"><Clock className="w-4 h-4 text-purple-600" /></div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Time Card{userName ? ` — ${userName}` : ""}</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">Clock-in/out history for the selected window.</p>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-3 py-1.5 text-slate-700 dark:text-slate-200"
            data-testid="timecard-range-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days (bi-weekly)</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          <Button variant="outline" size="sm" onClick={load} className="rounded-lg" data-testid="timecard-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" onClick={handle_print} className="rounded-lg bg-purple-600 hover:bg-purple-700 text-white" data-testid="timecard-print-btn">
            <Printer className="w-3.5 h-3.5 mr-1" /> Print
          </Button>
        </div>
      </div>

      <div className="bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-900 rounded-lg p-4 mb-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-[11px] text-purple-700 dark:text-purple-300 uppercase tracking-wide font-medium">Total hours</p>
          <p className="text-2xl font-bold text-purple-700 dark:text-purple-300" data-testid="timecard-total-hours">{format_dur(total_hours)}</p>
        </div>
        <p className="text-xs text-purple-700/80 dark:text-purple-300/80">
          {days_grouped.length} day(s) &middot; {entries.length} punch(es) &middot; window {days}d
        </p>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-400 dark:text-slate-500 text-sm">Loading…</div>
      ) : days_grouped.length === 0 ? (
        <div className="text-center py-8 text-slate-400 dark:text-slate-500 text-sm" data-testid="timecard-empty">No clock entries in this window.</div>
      ) : (
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
          <table className="w-full text-sm" data-testid="timecard-table">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-left">
              <tr>
                <th className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Date</th>
                <th className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Clock In</th>
                <th className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Clock Out</th>
                <th className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 text-right">Hours</th>
              </tr>
            </thead>
            <tbody>
              {days_grouped.map((d) => (
                d.punches.map((p, idx) => (
                  <tr key={`${d.date}-${idx}`} className="border-t border-slate-100 dark:border-slate-800">
                    {idx === 0 ? (
                      <td rowSpan={d.punches.length} className="px-3 py-2 align-top text-slate-900 dark:text-white font-medium">
                        <div>{d.date}</div>
                        <div className={`text-xs font-semibold mt-0.5 ${d.has_open ? "text-amber-600" : "text-purple-600"}`}>{format_dur(d.hours)}{d.has_open ? " (open)" : ""}</div>
                      </td>
                    ) : null}
                    <td className="px-3 py-2 text-slate-700 dark:text-slate-200">{format_time(p.clock_in)}</td>
                    <td className="px-3 py-2 text-slate-700 dark:text-slate-200">
                      {p.clock_out ? format_time(p.clock_out) : <span className="text-amber-600 font-medium">Still clocked in</span>}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-700 dark:text-slate-200 font-mono text-xs">
                      {p.clock_in && p.clock_out ? format_dur((new Date(p.clock_out) - new Date(p.clock_in)) / (1000 * 60 * 60)) : "—"}
                    </td>
                  </tr>
                ))
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
